import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  RefreshCw,
  Download,
  CheckCircle2,
  Award,
  FileText,
  Map as MapIcon,
  GraduationCap,
  Sparkles,
  ExternalLink,
  AlertTriangle,
  Loader2,
} from "lucide-react";
import ResourceLibrary from "@/components/ResourceLibrary";
import {
  EDUCATOR_RESOURCES,
  EDUCATOR_RESOURCES_FOLDER,
  BUSINESS_TEMPLATES,
  BUSINESS_TEMPLATES_FOLDER,
} from "@/data/externalResources";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEMO_EMAIL = "student-complete@test.com";

/**
 * Admin Demo Reports Hub — single sales-friendly page that:
 *   1. Shows current state of the `student-complete@test.com` showcase account
 *      (lessons completed, session assessments, badges, AI plan version, etc.).
 *   2. Lets an admin re-seed the demo with one click (POST /api/admin/demo-mode/reset).
 *   3. Provides one-click PDF downloads of the Business Plan and Journey Map
 *      for that student, without requiring a separate login.
 */
export default function AdminDemoReportsPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reseeding, setReseeding] = useState(false);
  const [reseedResult, setReseedResult] = useState(null);
  const [downloading, setDownloading] = useState(null); // "biz-plan" | "journey-map" | null

  const loadStatus = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/admin/demo-mode/status`);
      setStatus(r.data);
    } catch (e) {
      setError(
        e?.response?.status === 403
          ? "Admin access required. Login as admin@lemonstand.com to view this page."
          : "Couldn't load demo status — please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  const handleReseed = async () => {
    if (reseeding) return;
    setReseeding(true);
    setReseedResult(null);
    try {
      const r = await axios.post(`${API}/admin/demo-mode/reset`);
      setReseedResult({ ok: true, ...r.data });
      await loadStatus();
    } catch (e) {
      setReseedResult({
        ok: false,
        error: e?.response?.data?.detail || "Re-seed failed.",
      });
    } finally {
      setReseeding(false);
    }
  };

  const downloadPdf = async (kind) => {
    if (downloading) return;
    const userId = status?.demo_user?.user_id || statusUserId;
    // Fallback: fetch user_id explicitly if not in payload
    let sid = userId;
    if (!sid) {
      try {
        const probe = await axios.get(`${API}/admin/users?email=${encodeURIComponent(DEMO_EMAIL)}`);
        sid = probe?.data?.users?.[0]?.id;
      } catch {
        /* ignore */
      }
    }
    if (!sid) {
      // Final fallback: server-known mapping endpoint via demo-mode/status returns the email but not id.
      // Read via the status endpoint with includeId param if present, or hard-fallback below.
      sid = "24b4075e-955b-4a3e-a926-dab2916cf9a7"; // student-complete demo user_id (stable)
    }

    const url =
      kind === "biz-plan"
        ? `${API}/admin/student/${sid}/business-plan/pdf`
        : `${API}/cte/student/${sid}/journey-map/pdf`;

    setDownloading(kind);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const filename =
        kind === "biz-plan"
          ? "Jordan_Complete_Business_Plan.pdf"
          : "Jordan_Complete_Journey_Map.pdf";
      const a = document.createElement("a");
      a.href = window.URL.createObjectURL(blob);
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(a.href);
    } catch (e) {
      alert(`Download failed: ${e.message || e}`);
    } finally {
      setDownloading(null);
    }
  };

  const statusUserId = "24b4075e-955b-4a3e-a926-dab2916cf9a7";
  const u = status?.demo_user;
  const last = status?.last_activation;

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-orange-50 to-amber-50">
      {/* Header */}
      <header className="bg-white border-b border-yellow-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-gray-700 hover:text-gray-900 font-semibold"
            data-testid="demo-reports-back"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <h1 className="text-base sm:text-lg font-black text-gray-900 inline-flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-yellow-500" /> Demo Reports Hub
          </h1>
          <button
            onClick={loadStatus}
            disabled={loading}
            className="inline-flex items-center gap-1 text-xs font-bold text-gray-600 hover:text-gray-900 disabled:opacity-50"
            data-testid="demo-reports-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {/* Intro */}
        <div className="bg-white ring-1 ring-yellow-200 rounded-3xl p-6 mb-6 shadow-sm">
          <h2 className="text-xl sm:text-2xl font-black text-gray-900 mb-2">
            Sales-demo control center
          </h2>
          <p className="text-sm text-gray-700 leading-relaxed">
            One-click access to the <strong>{DEMO_EMAIL}</strong> showcase account.
            Re-seed it before a demo so the data looks fresh, then download polished
            PDF reports straight from this page — no separate login needed.
          </p>
        </div>

        {error && (
          <div
            className="bg-rose-50 ring-1 ring-rose-300 text-rose-800 rounded-2xl p-4 mb-6 flex items-start gap-3"
            data-testid="demo-reports-error"
          >
            <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
            <div>
              <p className="font-bold">Couldn't load demo status</p>
              <p className="text-sm">{error}</p>
            </div>
          </div>
        )}

        {loading && !status && (
          <div className="text-center py-20 text-gray-500">
            <Loader2 className="w-6 h-6 animate-spin inline" /> Loading…
          </div>
        )}

        {status && u && (
          <>
            {/* State of the account */}
            <section className="bg-white ring-1 ring-gray-200 rounded-3xl p-6 mb-6 shadow-sm">
              <div className="flex items-baseline justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900">Account state</h3>
                <span className="text-xs font-bold text-gray-500" data-testid="demo-reports-email">
                  {u.email}
                </span>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <StatCard
                  icon={<GraduationCap className="w-4 h-4" />}
                  label="Lessons complete"
                  value={`${u.lessons_completed}`}
                  testId="demo-reports-lessons"
                  ok={u.lessons_completed >= 42}
                />
                <StatCard
                  icon={<CheckCircle2 className="w-4 h-4" />}
                  label="Session reflections"
                  value={`${u.session_assessments} / 7`}
                  testId="demo-reports-assessments"
                  ok={u.session_assessments >= 7}
                />
                <StatCard
                  icon={<FileText className="w-4 h-4" />}
                  label="Capstone fields"
                  value={`${u.capstone_fields_count} / 7`}
                  testId="demo-reports-capstone"
                  ok={u.capstone_fields_count >= 7}
                />
                <StatCard
                  icon={<Award className="w-4 h-4" />}
                  label="Badges"
                  value={`${u.badges_count}`}
                  testId="demo-reports-badges"
                  ok={u.badges_count >= 1}
                />
                <StatCard
                  icon={<Sparkles className="w-4 h-4" />}
                  label="Points"
                  value={`${u.points}`}
                  testId="demo-reports-points"
                  ok={u.points >= 1000}
                />
                <StatCard
                  icon={<FileText className="w-4 h-4" />}
                  label="AI plan version"
                  value={u.ai_plan_version ? `v${u.ai_plan_version}` : "—"}
                  testId="demo-reports-aiplan"
                  ok={u.has_ai_plan}
                />
                <StatCard
                  icon={<FileText className="w-4 h-4" />}
                  label="AI plan sections"
                  value={`${u.ai_plan_sections || 0}`}
                  testId="demo-reports-aisections"
                  ok={(u.ai_plan_sections || 0) >= 5}
                />
                <StatCard
                  icon={<RefreshCw className="w-4 h-4" />}
                  label="Regenerations left"
                  value={
                    u.ai_plan_remaining_regenerations === null
                      ? "—"
                      : `${u.ai_plan_remaining_regenerations}`
                  }
                  testId="demo-reports-regens"
                  ok={(u.ai_plan_remaining_regenerations ?? 0) > 0}
                />
              </div>
              {last && (
                <p
                  className="mt-4 text-xs text-gray-500 italic"
                  data-testid="demo-reports-last-activation"
                >
                  Last re-seed: {new Date(last.created_at).toLocaleString()} (took{" "}
                  {last.elapsed_seconds}s) by {last.triggered_by_name}
                </p>
              )}
            </section>

            {/* Re-seed */}
            <section className="bg-white ring-1 ring-gray-200 rounded-3xl p-6 mb-6 shadow-sm">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Re-seed before a demo</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Repopulates 42 lessons + 7 reflections + 7 capstone fields, then
                    regenerates the AI Business Plan with version pinned to v1
                    (so the UI shows "2 regenerations remaining").
                  </p>
                </div>
                <button
                  onClick={handleReseed}
                  disabled={reseeding}
                  className="inline-flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-white px-5 py-3 rounded-full font-bold transition-colors shadow disabled:opacity-60 shrink-0"
                  data-testid="demo-reports-reseed"
                >
                  {reseeding ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Re-seeding…
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4" /> Re-seed now
                    </>
                  )}
                </button>
              </div>
              {reseedResult && (
                <div
                  className={`mt-2 text-sm rounded-2xl p-3 ${
                    reseedResult.ok
                      ? "bg-green-50 ring-1 ring-green-300 text-green-800"
                      : "bg-rose-50 ring-1 ring-rose-300 text-rose-800"
                  }`}
                  data-testid="demo-reports-reseed-result"
                >
                  {reseedResult.ok
                    ? `✓ Re-seeded in ${reseedResult.elapsed_seconds}s — lessons: ${reseedResult.demo_user?.lessons_completed}, AI plan: v${reseedResult.demo_user?.ai_plan_version}`
                    : `✗ ${reseedResult.error}`}
                </div>
              )}
            </section>

            {/* Downloads */}
            <section className="bg-white ring-1 ring-gray-200 rounded-3xl p-6 mb-6 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Generate & download reports</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <DownloadCard
                  icon={<FileText className="w-6 h-6" />}
                  title="Mini Business Plan (PDF)"
                  description="The full one-pager kids share with parents — branded, badges, persona, money plan, marketing, launch date."
                  buttonLabel="Download PDF"
                  onClick={() => downloadPdf("biz-plan")}
                  loading={downloading === "biz-plan"}
                  testId="demo-reports-download-biz-plan"
                  color="amber"
                />
                <DownloadCard
                  icon={<MapIcon className="w-6 h-6" />}
                  title="Journey Map (PDF)"
                  description="Polished one-page Journey Map showing missions completed, reflections, and DISC profile."
                  buttonLabel="Download PDF"
                  onClick={() => downloadPdf("journey-map")}
                  loading={downloading === "journey-map"}
                  testId="demo-reports-download-journey-map"
                  color="sky"
                />
              </div>
            </section>

            {/* Quick links to the live student views */}
            <section className="bg-white ring-1 ring-gray-200 rounded-3xl p-6 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-3">Or open the live student dashboard</h3>
              <p className="text-sm text-gray-600 mb-4">
                For a richer screen-capture demo, log out and sign in as{" "}
                <code className="bg-gray-100 rounded px-2 py-0.5 text-xs">{DEMO_EMAIL}</code>{" "}
                with password{" "}
                <code className="bg-gray-100 rounded px-2 py-0.5 text-xs">student123</code>.
              </p>
              <div className="flex flex-wrap gap-2">
                <a
                  href="/login"
                  className="inline-flex items-center gap-2 bg-white ring-2 ring-gray-300 hover:ring-yellow-400 px-4 py-2 rounded-full font-bold text-sm text-gray-800 transition-colors"
                  data-testid="demo-reports-go-login"
                >
                  Go to login <ExternalLink className="w-3.5 h-3.5" />
                </a>
                <a
                  href="/elementary/demo"
                  className="inline-flex items-center gap-2 bg-white ring-2 ring-gray-300 hover:ring-yellow-400 px-4 py-2 rounded-full font-bold text-sm text-gray-800 transition-colors"
                  data-testid="demo-reports-go-elementary"
                >
                  Open CEO City <ExternalLink className="w-3.5 h-3.5" />
                </a>
                <a
                  href="/admin"
                  className="inline-flex items-center gap-2 bg-white ring-2 ring-gray-300 hover:ring-yellow-400 px-4 py-2 rounded-full font-bold text-sm text-gray-800 transition-colors"
                  data-testid="demo-reports-go-admin"
                >
                  Admin Dashboard <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>
            </section>

            {/* Sales-ready downloadable kits — Drive */}
            <section className="mt-6 grid lg:grid-cols-2 gap-6">
              <ResourceLibrary
                title="Educator Resources"
                description="Facilitator guides, classroom activities, sponsorship deck — share these with prospective districts and pilot schools."
                folderUrl={EDUCATOR_RESOURCES_FOLDER}
                files={EDUCATOR_RESOURCES}
                testId="demo-reports-educator-resources"
                accent="sky"
              />
              <ResourceLibrary
                title="Business Templates"
                description="Student-facing worksheets, calculators, and pitch templates — useful when prospects ask 'what do students actually do?'"
                folderUrl={BUSINESS_TEMPLATES_FOLDER}
                files={BUSINESS_TEMPLATES}
                testId="demo-reports-business-templates"
                accent="yellow"
              />
            </section>
          </>
        )}
      </main>
    </div>
  );
}

function StatCard({ icon, label, value, ok, testId }) {
  return (
    <div
      className={`rounded-2xl p-4 ring-1 ${
        ok ? "bg-green-50 ring-green-200" : "bg-amber-50 ring-amber-200"
      }`}
      data-testid={testId}
    >
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-gray-700 mb-1">
        <span className={ok ? "text-green-700" : "text-amber-700"}>{icon}</span>
        {label}
      </div>
      <div className="text-2xl font-black text-gray-900">{value}</div>
    </div>
  );
}

function DownloadCard({ icon, title, description, buttonLabel, onClick, loading, testId, color = "amber" }) {
  const palette = {
    amber: { bg: "bg-amber-50", ring: "ring-amber-200", chip: "bg-amber-500", btn: "bg-amber-500 hover:bg-amber-600" },
    sky:   { bg: "bg-sky-50",   ring: "ring-sky-200",   chip: "bg-sky-500",   btn: "bg-sky-500 hover:bg-sky-600" },
  }[color] || { bg: "bg-amber-50", ring: "ring-amber-200", chip: "bg-amber-500", btn: "bg-amber-500 hover:bg-amber-600" };

  return (
    <div className={`${palette.bg} ring-1 ${palette.ring} rounded-2xl p-5 flex flex-col`}>
      <div className="flex items-center gap-3 mb-3">
        <div className={`${palette.chip} text-white w-10 h-10 rounded-xl flex items-center justify-center shadow`}>
          {icon}
        </div>
        <h4 className="text-base font-bold text-gray-900">{title}</h4>
      </div>
      <p className="text-sm text-gray-700 leading-relaxed mb-4 flex-1">{description}</p>
      <button
        onClick={onClick}
        disabled={loading}
        className={`${palette.btn} text-white px-4 py-2.5 rounded-full font-bold inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-60`}
        data-testid={testId}
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" /> Generating…
          </>
        ) : (
          <>
            <Download className="w-4 h-4" /> {buttonLabel}
          </>
        )}
      </button>
    </div>
  );
}
